import React from "react";

interface ListItem {
  id: string;
  title: string;
  subtitle?: string;
  badge?: string;
}

function isListItem(value: unknown): value is ListItem {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.id === "string" && typeof obj.title === "string";
}

/**
 * Flat list with hover affordance. Payload shape mirrors the workspace
 * render-spec compiler:
 *   { items: Array<{ id, title, subtitle?, badge? }>, objectKind? }
 *
 * When the agent emits an `objectKind` (e.g. "deal", "contact",
 * "project"), each item becomes globally selectable — clicking it
 * emits an ObjectRef to the chat composer via WorkspaceShell's
 * data-object-* protocol. Without `objectKind`, items render as
 * presentation-only.
 *
 * Visual spec: see .list-row in sandbox-styles.css. Each row is a
 * surface card with 10/12 padding and 1px border that hovers to
 * border-hover.
 */
export default function ListBlock({ payload }: { payload: Record<string, unknown> }) {
  const rawItems = Array.isArray(payload.items) ? payload.items : [];
  const items = rawItems.filter(isListItem);
  const objectKind = typeof payload.objectKind === "string" ? payload.objectKind : undefined;

  if (items.length === 0) {
    return (
      <div
        aria-label="list-empty"
        className="text-xs italic"
        style={{ color: "var(--c-text-faint)" }}
      >
        No items in this list.
      </div>
    );
  }

  return (
    <div aria-label="list" className="flex flex-col" style={{ gap: 2 }}>
      {items.map((item) => (
        <ListRow key={item.id} item={item} objectKind={objectKind} />
      ))}
    </div>
  );
}

function ListRow({ item, objectKind }: { item: ListItem; objectKind?: string }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      data-list-id={item.id}
      data-object-id={objectKind ? item.id : undefined}
      data-object-kind={objectKind}
      className="flex items-center justify-between"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "10px 12px",
        borderRadius: 6,
        backgroundColor: "var(--c-surface)",
        border: `1px solid ${hovered ? "var(--c-border-hover, var(--c-border))" : "var(--c-border)"}`,
        transition: "border-color 150ms ease",
      }}
    >
      <div className="flex-1 min-w-0">
        <div className="font-medium" style={{ fontSize: 13, color: "var(--c-text)" }}>
          {item.title}
        </div>
        {item.subtitle && (
          <div className="text-xs" style={{ color: "var(--c-text-secondary)", marginTop: 2 }}>
            {item.subtitle}
          </div>
        )}
      </div>
      {item.badge && (
        <span
          className="font-medium"
          style={{
            fontSize: 11,
            padding: "2px 8px",
            borderRadius: 4,
            backgroundColor: "var(--c-surface-2, var(--c-surface))",
            color: "var(--c-text-secondary)",
            border: "1px solid var(--c-border)",
            marginLeft: 8,
            flexShrink: 0,
          }}
        >
          {item.badge}
        </span>
      )}
    </div>
  );
}
